import React from 'react';
import { observer } from 'mobx-react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material';
import type AdminCompaniesStore from '../../store/adminCompanies/adminCompaniesStore';
import CompanyField from '../../features/purchase/CompanyField';

/**
 * The New company dialog of the Companies tab: the purchase form's company field and an invoice email,
 * both required. Save sends the company the field resolved to; a refusal shows as the backend sent it.
 * A thin observer over the admin companies store.
 */
export default observer(function NewCompanyDialog({ adminCompaniesStore }: { adminCompaniesStore: AdminCompaniesStore }) {
  const { isNewCompanyOpen, isCreating, canCreate, createError, newInvoiceEmail } = adminCompaniesStore;

  if (!isNewCompanyOpen) return null;

  const close = () => adminCompaniesStore.closeNewCompany();

  return (
    <Dialog open onClose={isCreating ? undefined : close} fullWidth maxWidth='sm'>
      <DialogTitle>New company</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ pt: 1 }}>
          <CompanyField field={adminCompaniesStore.newCompanyField} disabled={isCreating} />
          <TextField
            label='Invoice email'
            type='email'
            required
            value={newInvoiceEmail}
            onChange={(event) => adminCompaniesStore.setNewInvoiceEmail(event.target.value)}
            size='small'
            fullWidth
            disabled={isCreating}
            helperText='Where the company’s invoices are sent.'
          />
          {createError !== null && (
            <Typography variant='body2' color='error' role='alert'>
              {createError.message}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant='newSecondary' onClick={close} disabled={isCreating}>
          Cancel
        </Button>
        <Button variant='contained' onClick={() => void adminCompaniesStore.createCompany()} disabled={!canCreate}>
          {isCreating ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
});
